import {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { DisplayedFile } from 'components/common/gallery/filesStore';
import { createStrictOptionalContext } from 'helpers/context';

export interface ExportToDocumentationContextType {
  fileToExport: DisplayedFile | null;
  filesToExport: DisplayedFile[];
  isOpen: boolean;
  openExportDialog: (files: DisplayedFile | DisplayedFile[]) => void;
  closeExportDialog: () => void;
}

const {
  Provider: ExportToDocumentationContextProvider,
  useRequiredContext: useExportToDocumentationContext,
  useOptionalContext: useExportToDocumentationContextOptional,
} = createStrictOptionalContext<ExportToDocumentationContextType>(
  'useExportToDocumentation',
  'ExportToDocumentationProvider'
);

export function ExportToDocumentationProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [filesToExport, setFilesToExport] = useState<DisplayedFile[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const fileToExport = filesToExport[0] ?? null;

  const openExportDialog = useCallback(
    (files: DisplayedFile | DisplayedFile[]) => {
      setFilesToExport(Array.isArray(files) ? files : [files]);
      setIsOpen(true);
    },
    []
  );

  const closeExportDialog = useCallback(() => {
    setIsOpen(false);
    setFilesToExport([]);
  }, []);

  const value = useMemo(
    () => ({
      fileToExport,
      filesToExport,
      isOpen,
      openExportDialog,
      closeExportDialog,
    }),
    [filesToExport, isOpen, openExportDialog, closeExportDialog]
  );

  return (
    <ExportToDocumentationContextProvider value={value}>
      {children}
    </ExportToDocumentationContextProvider>
  );
}

export function useExportToDocumentation(): ExportToDocumentationContextType {
  return useExportToDocumentationContext();
}

export function useExportToDocumentationOptional(): ExportToDocumentationContextType | undefined {
  return useExportToDocumentationContextOptional();
}
